import {
  booleanAttribute,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  RADIO_GROUP,
  RadioGroupComponent,
} from '@app/shared/components/radio-buttons/radio-group/radio-group.component';

let nextUniqueId = 0;

@Directive()
export abstract class RadioButtonDirective<T> implements OnInit {
  private _value: T | null = null;
  private _checked: boolean = false;
  private _disabled: boolean = false;
  private _required: boolean = false;

  @Input() id: string = `${nextUniqueId++}`;
  @Input() name: string = '';
  @Input('aria-label') ariaLabel!: string;
  @Input('aria-labelledby') ariaLabelledby!: string;
  @Input('aria-describedby') ariaDescribedby!: string;

  radioGroup: RadioGroupComponent<T>;

  get inputId(): string {
    return `radio-group-item-${this.id}`;
  }

  @Input()
  get value(): any {
    return this._value;
  }
  set value(value: any) {
    if (this._value !== value) {
      this._value = value;
      if (this.radioGroup) {
        if (!this.checked) {
          this.checked = this.radioGroup.value === value;
        }
      }
    }
  }

  @Input({ transform: booleanAttribute })
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    if (this._checked !== value) {
      this._checked = value;
      if (value && this.radioGroup && this.radioGroup.value !== this.value) {
        this.radioGroup.selected = this;
      } else if (!value && this.radioGroup && this.radioGroup.value === this.value) {
        this.radioGroup.selected = null;
      }
    }
  }

  @Output() readonly change: EventEmitter<T> = new EventEmitter<T>();

  @Input({ transform: booleanAttribute })
  get disabled(): boolean {
    return this._disabled || (this.radioGroup !== null && this.radioGroup.disabled);
  }
  set disabled(value: boolean) {
    this._setDisabled(value);
  }

  @Input({ transform: booleanAttribute })
  get required(): boolean {
    return this._required || (this.radioGroup && this.radioGroup.required);
  }
  set required(value: boolean) {
    this._required = value;
  }

  @ViewChild('input') inputElement!: ElementRef<HTMLInputElement>;

  constructor() {
    this.radioGroup = inject<RadioGroupComponent<T>>(RADIO_GROUP, { optional: true })!;
  }

  ngOnInit() {
    if (this.radioGroup) {
      this.name = this.radioGroup.name;
    }
  }

  onChange(event: Event) {
    event.stopPropagation();

    if (!this.checked && !this.disabled) {
      const groupValueChanged = this.radioGroup && this.value !== this.radioGroup.value;
      this.checked = true;

      if (this.radioGroup) {
        this.radioGroup.onChange(this.value);
        if (groupValueChanged) {
          this.radioGroup.emitChangeEvent();
        }
      }
    }
  }

  protected _setDisabled(value: boolean) {
    if (this._disabled !== value) {
      this._disabled = value;
    }
  }
}
